import { queryClient } from '@/lib/queryClient.js';
import { APP_CONFIG_QUERY_KEY } from '@/hooks/useAppConfig.js';

/**
 * App config from the `/config/app` endpoint, fetched via react-query
 * (useAppConfig) on app load and cached in memory. Read fresh on each access so
 * values reflect the response as soon as it lands. On a full page reload the
 * cache is cleared, so the config is re-fetched.
 */
function getAppConfig() {
  return queryClient.getQueryData(APP_CONFIG_QUERY_KEY) || null;
}

/**
 * Booking constraints.
 *
 * Values backed by `/config/app` use getters reading the cached response. No
 * hardcoded fallback for server-sourced values — the modalPages gate blocks
 * until /config/app loads (and AppConfigLoader warms it at app root), so these
 * reflect the server only and never show a stale guess. Values with no API
 * source stay as plain (hardcoded) properties below.
 */
export const BOOKING_CONSTRAINTS = {
  // --- Automated from /config/app ---
  // booking.start_time_buffer is in MINUTES (direct).
  get MINIMUM_BOOKING_LEAD_TIME_MINUTES() {
    return getAppConfig()?.booking?.start_time_buffer;
  },
  // general.support_phone.
  get CONTACT_NUMBER() {
    return getAppConfig()?.general?.support_phone;
  },
  // general.support_email. Added 2026-08-26 — it was hardcoded next to the phone in
  // cancellationModificationRefund.jsx, so the two could drift from the config apart.
  get SUPPORT_EMAIL() {
    return getAppConfig()?.general?.support_email;
  },
  // booking.max_start_time_days is in DAYS (direct). ~90 ≈ old 3-month default.
  get MAX_BOOKING_ADVANCE_DAYS() {
    return getAppConfig()?.booking?.max_start_time_days;
  },
  // gratuity.gratuity_percentage_options is [{value,label}]; the UI needs a
  // flat array of percent numbers, so map to `value`.
  get DRIVER_TIP_OPTIONS() {
    const opts = getAppConfig()?.gratuity?.gratuity_percentage_options;
    // [] only as a structural guard so the UI's .map never crashes — not a
    // business default; the gate guarantees the real options are present.
    return Array.isArray(opts) ? opts.map((o) => (o && typeof o === 'object' ? o.value : o)) : [];
  },
  // Display text for one tip option. The 0 ("No gratuity") entry shows the admin-set label from
  // /config/app; every other option is just its percentage.
  tipLabel(value) {
    if (Number(value) !== 0) return `${value}%`;
    const opts = getAppConfig()?.gratuity?.gratuity_percentage_options;
    const zero = Array.isArray(opts) ? opts.find((o) => o && typeof o === 'object' && Number(o.value) === 0) : null;
    return zero?.label || 'No gratuity';
  },
  // general.otp_resend_timer is in SECONDS (direct).
  get OTP_RESEND_TIMER_SECONDS() {
    return getAppConfig()?.general?.otp_resend_timer;
  },
  // Minimum gap (MINUTES) before a booking's pickup. Shown on the cancellation
  // modal's "Ride Cancelled" screen. Prefers booking.minimum_time_buffer if the
  // API exposes it under that name, else the same lead-time value the rest of the
  // flow already gates on (booking.start_time_buffer) — they describe one setting.
  get MINIMUM_TIME_BUFFER_MINUTES() {
    const booking = getAppConfig()?.booking;
    return booking?.minimum_time_buffer ?? booking?.start_time_buffer;
  },
  // Smart-suggestion pickup buffers: booking.to_airport_extra_minutes /
  // from_airport_extra_minutes. Fallbacks keep the previous hardcoded values
  // when the config field is absent, so the pickup math never turns NaN.
  //
  // These are now the GLOBAL FALLBACK only. The cushion is per airport as of
  // 2026-08-04 — /config/airports carries a waiting_times bucket per direction ×
  // flight scope, and these globals apply only when the matching bucket is
  // unconfigured (total_minutes 0 / items []). Always read the cushion through
  // utils/airports.js → getAirportBufferMinutes(), never these getters directly.
  get TO_AIRPORT_BUFFER_MINUTES() {
    return getAppConfig()?.booking?.to_airport_extra_minutes ?? 180;
  },
  get FROM_AIRPORT_BUFFER_MINUTES() {
    return getAppConfig()?.booking?.from_airport_extra_minutes ?? 60;
  },
  // Where we actually operate: {enabled, center:{lat,lng}, radius_km}. Drives the Google
  // suggestion restriction in PlaceAutocomplete. /config/app is already fetched at app
  // root (AppConfigLoader in App.jsx), so this is populated before any typing happens.
  // Undefined until it lands ⇒ no restriction is applied.
  get SERVICE_AREA() {
    return getAppConfig()?.booking?.service_area;
  },

  // --- No API source yet: hardcoded here (centralized; automatable later) ---
  // Single country code (string). Phone-input default + location restriction.
  DEFAULT_COUNTRY: 'ca',
  // Time-picker granularity (minutes between selectable slots).
  TIME_SLOT_INTERVAL_MINUTES: 5,
  // Latest selectable pickup time of day (last bookable slot).
  LATEST_BOOKING_HOUR: 23,
  LATEST_BOOKING_MINUTE: 45,

  /**
   * How long a special request may be — the BACKEND's own cap, mirrored here.
   *
   * `api-docs/document.json` types `special_request` as `maxLength: 500` on
   * CreateTripRequest, UpdateTripRequest and ConfirmTripRequest alike, so 500 covers all
   * three things this portal does with one: book, edit, confirm. Without it in the UI the
   * limit was discovered by the SERVER, after submission — the booking flow reached "We
   * are processing your booking…" and then failed with "Special request cannot exceed 500
   * characters", with the field several screens behind the passenger.
   *
   * ⚠️ NOT a value /config/app publishes — this is a validation rule, not configuration,
   * and there is no field for it. If the backend relaxes the cap this number has to be
   * changed by hand, which is why it names the schemas above.
   *
   * ⚠️ The SUPPORT leg is different: CreateSupportBookingRequest allows 2000. The phone
   * desk is a separate portal with its own constant — do not "unify" these two.
   *
   * The booking plugin carries the identical constant in its own
   * `constants/api.js`; the two portals do not share code, so they are kept in step by
   * hand and both cite the same schemas.
   */
  SPECIAL_REQUEST_MAX_LENGTH: 500,
};

/**
 * Calculated trip policies from `/trips/calculate-policies`, cached via
 * react-query (usePolicies) when the booking modal opens — same lifecycle as
 * getAppConfig.
 *
 * Two failure modes: (1) the call fails → the modalPages gate blocks and shows
 * "Online Booking is unavailable now" (these getters aren't read on that path);
 * (2) the call succeeds but an OPTIONAL field is absent (API omits
 * free_cancellation_hours when free_cancellation_available is false, or the
 * waiting block when disabled) → safe markers: numbers → 0, flags → false. Those
 * mean "off / none", NOT a guessed value. Text is NEVER defaulted — a missing
 * sentence is an admin mistake, so we surface empty rather than fake copy.
 *
 * Key literal inlined (not imported from usePolicies) to avoid a circular import
 * booking.js → usePolicies → tripService → booking.js.
 */
function getPolicies() {
  return queryClient.getQueryData(['policies', 'calculate']) || null;
}

export const POLICY_CONSTANTS = {
  // True once the policies response is cached. Out-of-modal callers branch on
  // this to avoid showing 0/empty as if it were a real policy.
  get LOADED() {
    return !!getPolicies();
  },

  // --- Cancellation ---
  get FREE_CANCELLATION_AVAILABLE() {
    return getPolicies()?.cancellation?.free_cancellation_available ?? false;
  },
  get FREE_CANCELLATION_HOURS() {
    return getPolicies()?.cancellation?.free_cancellation_hours ?? 0;
  },
  get CANCELLATION_TEXT() {
    return getPolicies()?.cancellation?.text ?? '';
  },
  // Cancellation fee/refund bands (furthest-from-pickup first). Each tier:
  // {band, label, hours_before_pickup:{from,to}, refund_percentage, credit_percentage,
  //  kept_percentage, flat_fee_cents, is_free, ...}. Drives the Cancellation + Refund
  // tabs of the cancellationModificationRefund popup (refund reads refund_percentage).
  get CANCELLATION_TIERS() {
    return getPolicies()?.cancellation?.tiers ?? [];
  },
  // The no-show rule for THIS trip — {refund_percentage, credit_percentage,
  // kept_percentage, flat_fee_cents, text}. Already resolved to the trip's direction and
  // distance band, so it beats matching against the /config/policies catalog by hand.
  // Returns null while the endpoint doesn't send it (it is documented on /config/policies;
  // this getter is the self-healing path if it lands here too) — callers fall back to the
  // catalog, then to their own copy.
  get NO_SHOW() {
    return getPolicies()?.cancellation?.no_show ?? null;
  },

  // --- Modification (changes / reschedule) ---
  get FREE_EDITS_COUNT() {
    return getPolicies()?.modification?.how_many_free_edits ?? 0;
  },
  get EDITABLE_HOURS_BEFORE_PICKUP() {
    return getPolicies()?.modification?.editable_hours_before_pickup ?? 0;
  },
  get MODIFICATION_TEXT() {
    return getPolicies()?.modification?.text ?? '';
  },
  // Modification bands. Each tier: {band, label, hours_before_pickup, can_edit,
  // editable_fields:[]}. Only `scheduled_at` in editable_fields ⇒ pickup-time-only edit.
  get MODIFICATION_TIERS() {
    return getPolicies()?.modification?.tiers ?? [];
  },

  // --- Waiting time ---
  // The /calculate-policies waiting block switched to MINUTES on 2026-07-15:
  // `free_minutes`, `rate_cents_per_minute`, `max_wait_minutes` (the old
  // `free_seconds` / `rate_cents_per_second` / `max_wait_seconds` keys are gone).
  // These getters keep their existing *_SECONDS contract — consumers convert to
  // display units — by converting the minute-based response back to seconds
  // (durations ×60, rate ÷60). Missing/false → false; when disabled, forced to 0.
  get WAITING_ENABLED() {
    return getPolicies()?.waiting?.enabled ?? false;
  },
  get WAITING_TEXT() {
    return getPolicies()?.waiting?.text ?? '';
  },
  get WAITING_PICKUP_FREE_SECONDS() {
    const w = getPolicies()?.waiting;
    return w?.enabled ? (w?.pickup?.free_minutes ?? 0) * 60 : 0;
  },
  get WAITING_PICKUP_RATE_CENTS_PER_SECOND() {
    const w = getPolicies()?.waiting;
    return w?.enabled ? (w?.pickup?.rate_cents_per_minute ?? 0) / 60 : 0;
  },
  get WAITING_PICKUP_MAX_WAIT_SECONDS() {
    const w = getPolicies()?.waiting;
    return w?.enabled ? (w?.pickup?.max_wait_minutes ?? 0) * 60 : 0;
  },
  get WAITING_STOPOVER_FREE_SECONDS() {
    const w = getPolicies()?.waiting;
    return w?.enabled ? (w?.stopover?.free_minutes ?? 0) * 60 : 0;
  },
  get WAITING_STOPOVER_RATE_CENTS_PER_SECOND() {
    const w = getPolicies()?.waiting;
    return w?.enabled ? (w?.stopover?.rate_cents_per_minute ?? 0) / 60 : 0;
  },
};

// This portal's own public URL, used to build handoff links. NO default — `.env` is the
// single source. It used to fall back to `window.location.origin`, which is a guess that
// happens to be right when the portal is served from its canonical host and silently
// wrong the moment it is not (a preview deploy, a staging host, an embedded view), baking
// that wrong origin into links handed to passengers.
export const PASSENGER_PORTAL_URL = import.meta.env.VITE_PASSENGER_PORTAL_URL || '';


export const TRIP_DIRECTIONS = {
  FROM_AIRPORT: 'from_airport',
  TO_AIRPORT: 'to_airport',
  OTHERS: 'others',
};
